/**
 * CR313: Feature Flags Registry
 * 
 * Single source of truth for curated feature flags displayed in Admin UI.
 * Flags not in registry appear under "Other (Unregistered)" section.
 */

export const FEATURE_FLAG_CATEGORIES = [
  'Scanning',
  'Deal Cards',
  'Layout & Theme',
  'Merchants',
  'Pipeline',
  'Ops & Debug',
  'Experiments',
] as const;

export type FeatureFlagCategory = typeof FEATURE_FLAG_CATEGORIES[number];

export type FeatureFlagType = 'boolean' | 'rollout' | 'json';

export interface RolloutValue {
  mode: 'OFF' | 'ON' | 'PERCENT' | 'ALLOWLIST';
  percent: number;
  allowlist: string[];
}

export interface FeatureFlagRegistryEntry {
  key: string;
  label: string;
  category: FeatureFlagCategory;
  type: FeatureFlagType;
  defaultValue: unknown;
  description: string;
}

/**
 * Registry of all known feature flags.
 * Add new flags here to have them appear in the Admin UI.
 */
export const FEATURE_FLAGS_REGISTRY: FeatureFlagRegistryEntry[] = [
  // Scanning
  {
    key: 'scan_targets_use_directory_gate',
    label: 'Use Merchant Directory as Scan Gate',
    category: 'Scanning',
    type: 'boolean',
    defaultValue: false,
    description: 'When ON, scan target selection uses canonical views gated by Merchant Directory + active sources.',
  },
  
  // Deal Cards
  {
    key: 'dealCardSpecV2_enabled',
    label: 'Deal Cards V2 Enabled',
    category: 'Deal Cards',
    type: 'boolean',
    defaultValue: false,
    description: 'Master toggle for Deal Card Spec V2 rendering.',
  },
  {
    key: 'deal_card_v2_rollout',
    label: 'Deal Cards V2 Rollout',
    category: 'Deal Cards',
    type: 'rollout',
    defaultValue: { mode: 'OFF', percent: 0, allowlist: [] } as RolloutValue,
    description: 'Audience control for V2: OFF/ON/PERCENT/ALLOWLIST.',
  },
  
  // Ops & Debug
  {
    key: 'dealCardSpecV2_enabled__legacy_backup',
    label: 'Deal Cards V2 Enabled (Legacy Backup)',
    category: 'Ops & Debug',
    type: 'boolean',
    defaultValue: false,
    description: 'Backup value used during migrations/rollbacks. Not intended for day-to-day use.',
  },
  {
    key: 'ff_s12_diagnostics_dashboard_v1',
    label: 'S1.2 Diagnostics Dashboard',
    category: 'Ops & Debug',
    type: 'boolean',
    defaultValue: true,
    description: 'Show the S1.2 OCR diagnostics dashboard with health monitoring, page lifecycle, and debug bundle export.',
  },
  {
    key: 'ff_gate_cookbook_v1',
    label: 'Gate Cookbook V1 (No-AI Remediation)',
    category: 'Scanning',
    type: 'boolean',
    defaultValue: true,
    description: 'When ON, gated WEB reads try vendor CMP + global cookbook modules before calling Gate Detective AI.',
  },
  {
    key: 'ff_gate_patterns_auto_promote_v1',
    label: 'Gate Patterns Auto-Promote',
    category: 'Scanning',
    type: 'boolean',
    defaultValue: true,
    description: 'When ON, DRAFT gate patterns auto-promote to ACTIVE after 2 successes on distinct runs.',
  },
  {
    key: 'ff_gate_patterns_auto_degrade_v1',
    label: 'Gate Patterns Auto-Degrade',
    category: 'Scanning',
    type: 'boolean',
    defaultValue: true,
    description: 'When ON, ACTIVE gate patterns auto-degrade to DEGRADED after 3 consecutive failures.',
  },
  {
    key: 'ff_gate_patterns_admin_v1',
    label: 'Gate Patterns Admin UI',
    category: 'Ops & Debug',
    type: 'boolean',
    defaultValue: true,
    description: 'When ON, the Gate Patterns admin page is accessible at /admin/gate-patterns.',
  },
  // CR453: Flyer pages v3 split tables
  {
    key: 'ff_flyer_pages_v3_tables',
    label: 'Flyer Pages V3 Split Tables (Dual-Write)',
    category: 'Pipeline',
    type: 'boolean',
    defaultValue: false,
    description: 'Phase 4 dual-write gate: when ON, S1.1/S1.2/AI writers also write to flyer_page_assets, flyer_page_ocr_attempts, flyer_page_ai_attempts. Keep OFF until Phase 4 writers are complete.',
  },
  {
    key: 'S1_CAPTURE_CONTRACT_RECOVERY_V1',
    label: 'S1 Capture Contract Recovery V1',
    category: 'Pipeline',
    type: 'boolean',
    defaultValue: false,
    description: 'Phase 7 guardrails for S1.0c: contract-based stuck-run recovery, scoped reconciliation actions, and hardened auto-retry behavior.',
  },
  {
    key: 'S1_PIPELINE_TOLERANCES_V1',
    label: 'S1 Pipeline Tolerances V1',
    category: 'Pipeline',
    type: 'boolean',
    defaultValue: false,
    description: 'Enable tolerance-aware S1.0c policy decisions (complete-with-gaps pathways) while preserving strict mode as default.',
  },
];

/**
 * Get registry entry by key
 */
export function getRegistryEntry(key: string): FeatureFlagRegistryEntry | undefined {
  return FEATURE_FLAGS_REGISTRY.find(entry => entry.key === key);
}

/**
 * Get all registry keys
 */
export function getRegistryKeys(): string[] {
  return FEATURE_FLAGS_REGISTRY.map(entry => entry.key);
}

/**
 * Check if a key is in the registry
 */
export function isRegisteredFlag(key: string): boolean {
  return FEATURE_FLAGS_REGISTRY.some(entry => entry.key === key);
}

/**
 * Get flags grouped by category
 */
export function getFlagsByCategory(): Map<FeatureFlagCategory, FeatureFlagRegistryEntry[]> {
  const grouped = new Map<FeatureFlagCategory, FeatureFlagRegistryEntry[]>();
  
  for (const category of FEATURE_FLAG_CATEGORIES) {
    grouped.set(category, []);
  }
  
  for (const entry of FEATURE_FLAGS_REGISTRY) {
    const list = grouped.get(entry.category) || [];
    list.push(entry);
    grouped.set(entry.category, list);
  }
  
  // Sort each category by label
  for (const [category, entries] of grouped) {
    entries.sort((a, b) => a.label.localeCompare(b.label));
    grouped.set(category, entries);
  }
  
  return grouped;
}

/**
 * Validate rollout value schema
 */
export function isValidRolloutValue(value: unknown): value is RolloutValue {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  
  if (!['OFF', 'ON', 'PERCENT', 'ALLOWLIST'].includes(String(v.mode))) return false;
  if (typeof v.percent !== 'number' || v.percent < 0 || v.percent > 100) return false;
  if (!Array.isArray(v.allowlist)) return false;
  
  return true;
}

/**
 * Get default rollout value
 */
export function getDefaultRolloutValue(): RolloutValue {
  return { mode: 'OFF', percent: 0, allowlist: [] };
}
